import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Heart, Stethoscope } from '../components/Icons.jsx';

// NHS pay scale for resident doctors + consultant + GP, current cycle
// Aug 2025 → Jul 2026 (after the September 2024 BMA agreement applied
// its second-year uplift of ~4% from Aug 2025). All figures are annual
// basic full-time, England. Excludes on-call / banding / weekend /
// London weighting / locum. The 2026-27 cycle is set by the DDRB in
// summer 2026 — any further uplift comes on top.
// Source: NHS Employers + BMA pay circular (Aug 2025).
const stages = [
  { id: 'fy1', label: 'FY1', years: '0–1 yr', annual: 38081 },
  { id: 'fy2', label: 'FY2', years: '1–2 yrs', annual: 43688 },
  { id: 'st1-3', label: 'CT / ST 1–3', years: '2–5 yrs', annual: 54503 },
  { id: 'st4-5', label: 'ST 4–5', years: '5–7 yrs', annual: 65678 },
  { id: 'st6-8', label: 'ST 6–8', years: '7–10 yrs', annual: 73242 },
  { id: 'consultant', label: 'Consultant', years: '10+ yrs', annual: 109725 }
];

// Specialty multipliers — rough adjustment for typical earnings ceiling
// vs the NHS basic Consultant baseline. NHS consultant basic ranges
// £105k-£139k; private practice / banding / on-call bumps vary by specialty.
const specialties = {
  surgery: { label: 'Surgery (cardiothoracic, neuro, ortho)', emoji: '🩻', multiplier: 1.32, note: 'High private earning ceiling. Long training (8+ yrs post-FY2). Cardiothoracic and orthopaedics top earners.' },
  radiology: { label: 'Radiology', emoji: '🩻', multiplier: 1.22, note: 'Lifestyle-friendly. Strong private market for diagnostic and interventional work.' },
  anaesthetics: { label: 'Anaesthetics & Intensive Care', emoji: '💉', multiplier: 1.18, note: 'On-call bumps base meaningfully. Strong locum opportunities.' },
  cardiology: { label: 'Cardiology', emoji: '🫀', multiplier: 1.25, note: 'High procedural earning. Private clinics in major cities.' },
  derm: { label: 'Dermatology', emoji: '🧴', multiplier: 1.20, note: 'Daytime hours, strong private practice market.' },
  ophthal: { label: 'Ophthalmology', emoji: '👁️', multiplier: 1.22, note: 'Cataract list productivity drives high consultant earning.' },
  emergency: { label: 'Emergency Medicine', emoji: '🚑', multiplier: 1.15, note: 'Shift work, intense. Limited private. Generous unsocial-hours pay.' },
  obgyn: { label: 'Obstetrics & Gynaecology', emoji: '👶', multiplier: 1.15, note: 'High on-call. Significant private O&G market in major cities.' },
  paeds: { label: 'Paediatrics', emoji: '🧒', multiplier: 1.05, note: 'Limited private market beyond London. Strong NHS base + on-call.' },
  psych: { label: 'Psychiatry', emoji: '🧠', multiplier: 1.02, note: 'Daytime-friendly. Modest private market. Strong work-life balance.' },
  gp: { label: 'GP (partner)', emoji: '🩺', multiplier: 1.00, note: 'Partner pay c. £100–120k after costs. Salaried GP closer to £75–95k.' },
  gp_salaried: { label: 'GP (salaried)', emoji: '🩺', multiplier: 0.85, note: 'Salaried GP pay £75–95k typical. Less risk than partner; lower ceiling.' }
};

const studentQuotes = [
  { name: 'Y3 — Manchester', quote: '"First year is a marathon of memorising. Year 3 is when you finally feel like a doctor — clinical placements, real patients, real questions."' },
  { name: 'Y2 — Imperial', quote: '"Hardest part isn\'t the work, it\'s the unrelenting nature. The breaks are short and the holidays are full of placements."' },
  { name: 'Y4 — Edinburgh', quote: '"I underestimated how much I\'d love hospital teaching. Consultants who teach well change your career trajectory."' },
  { name: 'Y5 — Cambridge', quote: '"Finals year is humbling. You realise how much you still don\'t know — and that\'s the point."' },
  { name: 'FY1 — Leeds', quote: '"Medical school prepares you for exams. FY1 prepares you for being a doctor. The gap is bigger than I expected."' },
  { name: 'Y3 — Bristol', quote: '"The friends you make on placement are the ones who carry you through. Build that network early."' }
];

export default function DoctorSalaries() {
  const [stageIdx, setStageIdx] = useState(0);
  const [specId, setSpecId] = useState('surgery');

  const stage = stages[stageIdx];
  const spec = specialties[specId];

  // Trainees earn near the standard scale; consultants and GPs differ by specialty.
  const isConsultant = stage.id === 'consultant';
  const isGpSalaried = specId === 'gp_salaried' && isConsultant;
  const annual = useMemo(() => {
    if (!isConsultant) return stage.annual;
    return Math.round(stage.annual * spec.multiplier);
  }, [stage, spec, isConsultant]);

  return (
    <>
      <div className="page-head">
        <Link to="/apply?tab=decide" className="back-link"><ArrowLeft width="16" height="16" />Back to Deciding on medicine</Link>
        <h1 style={{ marginTop: 12 }}>Doctor salaries</h1>
        <p className="subtitle">NHS pay across training and consultant grades — by specialty.</p>
      </div>

      <div className="card" style={{ marginBottom: 18 }}>
        <h3 style={{ margin: '0 0 12px', fontSize: 17 }}>Career stage</h3>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 22 }}>
          {stages.map((s, i) => (
            <button key={s.id} onClick={() => setStageIdx(i)} style={pill(stageIdx === i)}>
              <strong>{s.label}</strong>
              <span style={{ fontSize: 11, fontWeight: 500, opacity: 0.7, marginLeft: 6 }}>{s.years}</span>
            </button>
          ))}
        </div>

        <h3 style={{ margin: '0 0 12px', fontSize: 17 }}>{isConsultant ? 'Specialty' : 'Specialty (affects consultant pay)'}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8 }}>
          {Object.entries(specialties).map(([id, s]) => (
            <button key={id} onClick={() => setSpecId(id)} style={chipBtn(specId === id)}>
              <span style={{ fontSize: 18 }}>{s.emoji}</span>
              <span style={{ flex: 1, textAlign: 'left' }}>{s.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="card" style={{ marginBottom: 18, borderLeft: '5px solid var(--brand-purple)' }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          {stage.label} · {spec.label}
        </div>
        <h2 style={{ margin: '8px 0 4px', fontSize: 'clamp(36px, 6vw, 52px)' }}>
          £{annual.toLocaleString()}
          <span style={{ color: 'var(--text-muted)', fontSize: 18, fontWeight: 500, marginLeft: 8 }}>/ year (basic)</span>
        </h2>
        <p style={{ margin: '4px 0 14px', color: 'var(--text-muted)', fontSize: 13 }}>
          Basic NHS only. Adds {isConsultant ? '~10–60%' : '~10–40%'} with on-call, banding, locum and private practice (specialty-dependent).
        </p>
        {isConsultant && <p style={{ margin: 0, fontSize: 14, color: 'var(--text-2)', padding: '10px 14px', background: 'var(--card-soft)', borderRadius: 10 }}><strong>Specialty note: </strong>{spec.note}</p>}
      </div>

      <div className="card" style={{ marginBottom: 18 }}>
        <h3 style={{ margin: '0 0 12px', fontSize: 17 }}>Across the career arc</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {stages.map((s) => {
            const v = s.id === 'consultant' ? Math.round(s.annual * spec.multiplier) : s.annual;
            const pctOfMax = (v / Math.max(...stages.map((x) => x.id === 'consultant' ? Math.round(x.annual * spec.multiplier) : x.annual))) * 100;
            return (
              <div key={s.id}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                  <span style={{ fontWeight: 600 }}>{s.label} <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>· {s.years}</span></span>
                  <span style={{ color: 'var(--text-2)', fontWeight: 600 }}>£{v.toLocaleString()}</span>
                </div>
                <div style={{ height: 10, background: 'var(--card-soft)', borderRadius: 99, overflow: 'hidden' }}>
                  <div style={{ width: `${pctOfMax}%`, height: '100%', background: 'linear-gradient(90deg, var(--brand-purple), var(--brand-teal))', transition: 'width 350ms ease' }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 32 }}>
        Source: NHS Employers / BMA 2024–25 pay circular. Numbers are basic annual; final take-home varies considerably with on-call (banding), unsocial-hours uplifts, locum and private practice.
      </p>

      <h2 style={{ margin: '0 0 14px', fontSize: 22 }}>Life as a medical student</h2>
      <div className="r-grid-2" style={{ marginBottom: 24 }}>
        {studentQuotes.map((q) => (
          <div key={q.name} className="card" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <p style={{ margin: 0, fontFamily: 'Georgia, serif', fontSize: 15, lineHeight: 1.55, color: 'var(--text)' }}>{q.quote}</p>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{q.name}</div>
          </div>
        ))}
      </div>
    </>
  );
}

function pill(active) {
  return {
    padding: '8px 14px', borderRadius: 99, fontSize: 13, cursor: 'pointer',
    border: `1px solid ${active ? 'var(--brand-purple)' : 'var(--border)'}`,
    background: active ? 'var(--accent-soft)' : '#fff',
    color: active ? 'var(--brand-deep)' : 'var(--text-2)',
    fontWeight: 600
  };
}

function chipBtn(active) {
  return {
    padding: '10px 12px', borderRadius: 10, fontSize: 13.5, cursor: 'pointer',
    border: `1px solid ${active ? 'var(--brand-purple)' : 'var(--border)'}`,
    background: active ? 'var(--accent-soft)' : '#fff',
    color: active ? 'var(--brand-deep)' : 'var(--text-2)',
    fontWeight: active ? 600 : 500,
    display: 'flex', alignItems: 'center', gap: 8
  };
}
